import { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router";

import BrowserHistoryContainer from "./components/browserHistoryContainer/BrowserHistoryContainer";
import Layout from "./components/layout/Layout";
import ProtectedRoute from "./components/protectedRoute/ProtectedRoute";
import Favicon from "./components/favicon/Favicon";
import Loader from "./components/loader/Loader";
import ClarityScript from "./utils/MicrosoftClarity/ClarityScript";
import BackToTop from "./components/backToTop/BackToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import { getPageTitle } from "./utils/titleUtils";
import DisclaimerModal from "./components/modal/DisclaimerModal";
import { getDisclaimer } from "./api/blackbirdApiService";
// import GoogleTagManager from "./utils/GoogleTagManager/GoogleTagManager";

const Auctions = lazy(() => import("./pages/Auctions/Auctions"));
const Auction = lazy(() => import("./pages/Auction/Auction"));
const Register = lazy(() => import("./pages/Register/Register"));
const Login = lazy(() => import("./pages/Login/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword/ResetPassword"));
const Watchlist = lazy(() => import("./pages/Watchlist/Watchlist"));
const Ringman = lazy(() => import("./pages/RingmanV2/Ringman"));
const Auctioneer = lazy(() => import("./pages/Auctioneer/Auctioneer"));
const Presenter = lazy(() => import("./pages/Presenter/PresenterV2"));
const PresenterV3 = lazy(() => import("./pages/Presenter/PresenterV3"));
const Messenger = lazy(() => import("./pages/Messenger/Messenger"));
const Settings = lazy(() => import("./pages/Settings/Settings"));
const OnlineAuction = lazy(() => import("./pages/OnlineAuction/OnlineAuction"));
const NoPageFound = lazy(() => import("./pages/NoPageFound/NoPageFound"));
// const InputVideo = lazy(() => import("./pages/VideoControls/InputVideo"));
const ItemDetail = lazy(() => import("./pages/ItemDetails/ItemDetails"));
const PreviewPage = lazy(() => import("./pages/VideoControls/PreviewPage"));
const InputVideo = lazy(() => import("./pages/VideoControls/InputVideo"));
const PhenixHostControls = lazy(() => import("./pages/VideoControls/HostControls"));
const LiveAuctioneerDashboard = lazy(() => import("./pages/LiveAuctioneerDashboard/LiveAuctioneerDashboard"));
import useUserStore from "./store/useUserStore";
import useStreamingProviderStore from "./store/useStreamingProviderStore";
import featureFlagStore from "./store/featureFlagStore";
import useFeatureFlagStore from "./store/useFeatureFlagStore";
import { PERMISSIONS } from "./utils/constants";

function App() {
  const { isAuthenticated = false, handoff, checkToken, logout } = useUserStore();
  const { streamingProvider } = useStreamingProviderStore();
  const { enableOnlineAuction, isLoaded: featureFlagsLoaded } = useFeatureFlagStore();

  const [loggingIn, setLoggingIn] = useState(true);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [disclaimerContent, setDisclaimerContent] = useState("");

  // Fetch disclaimer settings on mount (once per session)
  useEffect(() => {
    if (sessionStorage.getItem("disclaimerDismissed")) return;

    getDisclaimer()
      .then((data) => {
        if (data && data.showDisclaimer && data.showDisclaimerContent) {
          setDisclaimerContent(data.showDisclaimerContent);
          setDisclaimerOpen(true);
        } else {
          sessionStorage.setItem("disclaimerDismissed", "true");
        }
      })
      .catch(() => {
        // Silently fail - disclaimer is non-critical
      });
  }, []);

  const handleDisclaimerClose = (open) => {
    setDisclaimerOpen(open);
    if (!open) {
      sessionStorage.setItem("disclaimerDismissed", "true");
    }
  };

  // Set page title immediately on mount to prevent flash
  useEffect(() => {
    document.title = getPageTitle();
  }, []);

  // Fetch feature flags once authenticated
  useEffect(() => {
    featureFlagStore.getState().fetchFeatureFlags();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      setLoggingIn(false);
    } else {
      // HANDOFF AUTHENTICATION
      const urlParams = new URLSearchParams(window.location.search);
      const paramsObject = {};

      for (const [key, value] of urlParams.entries()) {
        paramsObject[key] = value;
      }

      let { token } = paramsObject;

      if (token) {
        async function handoffAuthentication() {
          await handoff(token);
        }
        handoffAuthentication().then(() => setLoggingIn(false));
      } else {
        setLoggingIn(false);
      }
    }

    const parsedUrl = new URL(window.location.href);
    parsedUrl.searchParams.delete("token");

    window.history.pushState(null, null, parsedUrl.toString());
  }, []);

  useEffect(() => {
    const handleCheckToken = () => checkToken();

    handleCheckToken();

    const interval = setInterval(() => {
      handleCheckToken();
    }, 900000); //CHECK TOKEN EVERY 15 MINS

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    window.addEventListener("storage", handleAuthChange);

    return () => window.removeEventListener("storage", handleAuthChange);
  }, []);

  const handleAuthChange = () => {
    try {
      const raw = localStorage.getItem("user-storage");
      if (!raw) return;
      const userStorage = JSON.parse(raw);
      const { isAuthenticated, isSuperUser } = userStorage?.state ?? {};
      if (!isAuthenticated && !isSuperUser) {
        logout();
      }
    } catch {
      // Malformed JSON in storage — ignore
    }
  };

  if (loggingIn) {
    return (
      <div className="flex h-svh">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <title>{getPageTitle()}</title>
      <Favicon href={`/${import.meta.env.VITE_FAVICON}`} />
      <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/auctions" replace />} />
          <Route element={<Layout />}>
            <Route
              path="/login"
              element={
                <Suspense fallback={<Loader />}>
                  <Login />
                </Suspense>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <Suspense fallback={<Loader />}>
                  <ForgotPassword />
                </Suspense>
              }
            />
            <Route
              path="/reset/:code"
              element={
                <Suspense fallback={<Loader />}>
                  <ResetPassword />
                </Suspense>
              }
            />
            <Route element={<BrowserHistoryContainer />}>
              <Route
                path="/auctions"
                element={
                  <Suspense fallback={<Loader />}>
                    <Auctions />
                  </Suspense>
                }
              />
              <Route element={<ProtectedRoute />}>
                <Route
                  path="/watchlist"
                  element={
                    <Suspense fallback={<Loader />}>
                      <Watchlist />
                    </Suspense>
                  }
                />
              </Route>
              <Route
                path="/auction/:id"
                element={
                  <Suspense fallback={<Loader />}>
                    <Auction />
                  </Suspense>
                }
              />
              <Route
                path="/online-auction/:id"
                element={
                  !featureFlagsLoaded ? (
                    <Loader />
                  ) : enableOnlineAuction ? (
                    <Suspense fallback={<Loader />}>
                      <OnlineAuction />
                    </Suspense>
                  ) : (
                    <Navigate to="/auctions" replace />
                  )
                }
              />
              <Route
                path="/auction/:auctionId/item/:itemId"
                element={
                  <Suspense fallback={<Loader />}>
                    <ItemDetail />
                  </Suspense>
                }
              />
              <Route
                path="/online-auction/:auctionId/item/:itemId"
                element={
                  !featureFlagsLoaded ? (
                    <Loader />
                  ) : enableOnlineAuction ? (
                    <Suspense fallback={<Loader />}>
                      <ItemDetail />
                    </Suspense>
                  ) : (
                    <Navigate to="/auctions" replace />
                  )
                }
              />
              <Route
                path="/register/:id"
                element={
                  <Suspense fallback={<Loader />}>
                    <Register />
                  </Suspense>
                }
              />
              {/* <Route element={<ProtectedRoute permission={PERMISSIONS.Auction_AuctionClerk} />}> */}
              <Route
                path="/preview"
                element={
                  <Suspense fallback={<Loader />}>
                    <PreviewPage />
                  </Suspense>
                }
              />
              {/* </Route> */}
              <Route element={<ProtectedRoute permission={PERMISSIONS.Auction_AuctionClerk} />}>
                <Route
                  path="/settings/:id"
                  element={
                    <>
                      <title>{getPageTitle("Event Settings")}</title>
                      <Suspense fallback={<Loader />}>
                        <Settings />
                      </Suspense>
                    </>
                  }
                />
              </Route>
              <Route
                path="*"
                element={
                  <Suspense fallback={<Loader />}>
                    <NoPageFound />
                  </Suspense>
                }
              />
            </Route>
          </Route>

          <Route element={<ProtectedRoute permission={PERMISSIONS.Auction_AuctionClerk} />}></Route>

          <Route element={<ProtectedRoute permission={PERMISSIONS.Auction_Auctioneer} />}>
            <Route
              path="/auctioneer/:id"
              element={
                <>
                  <title>{getPageTitle("Auctioneer")}</title>
                  <Suspense
                    fallback={
                      <div className="h-svh flex items-center">
                        <Loader />
                      </div>
                    }
                  >
                    <Auctioneer />
                  </Suspense>
                </>
              }
            />
          </Route>

          <Route element={<ProtectedRoute permission={PERMISSIONS.Auction_AuctionClerk} />}>
            <Route
              path="/ringman/:id"
              element={
                <>
                  <title>{getPageTitle("Virtual Ringman")}</title>
                  <Suspense
                    fallback={
                      <div className="h-svh w-full flex items-center justify-center">
                        <Loader />
                      </div>
                    }
                  >
                    <Ringman />
                  </Suspense>
                </>
              }
            />
          </Route>

          <Route element={<ProtectedRoute permission={PERMISSIONS.Auction_AuctionClerk} />}>
            <Route
              path="/ringman/:id/video"
              element={
                <>
                  <title>{getPageTitle("Broadcast Settings")}</title>
                  <Suspense
                    fallback={
                      <div className="h-svh w-full flex items-center justify-center">
                        <Loader />
                      </div>
                    }
                  >
                    {streamingProvider === "phenix" ? <PhenixHostControls /> : <InputVideo />}
                  </Suspense>
                </>
              }
            />
          </Route>

          <Route element={<ProtectedRoute permission={PERMISSIONS.Auction_Presenter} />}>
            <Route
              path="/presenter/:id"
              element={
                <>
                  <title>{getPageTitle("Presenter")}</title>
                  <Suspense
                    fallback={
                      <div className="flex h-svh">
                        <Loader />
                      </div>
                    }
                  >
                    <Presenter />
                  </Suspense>
                </>
              }
            />
            <Route
              path="/presenter-v3/:id"
              element={
                <>
                  <title>{getPageTitle("Presenter")}</title>
                  <Suspense
                    fallback={
                      <div className="flex h-svh">
                        <Loader />
                      </div>
                    }
                  >
                    <PresenterV3 />
                  </Suspense>
                </>
              }
            />
          </Route>

          <Route element={<ProtectedRoute permission={PERMISSIONS.Auction_Auctioneer} />}>
            <Route
              path="/auction-control-center/:id"
              element={
                !featureFlagsLoaded ? (
                    <Loader />
                  ) : enableOnlineAuction ? (
                  <Suspense fallback={<Loader />}>
                    <LiveAuctioneerDashboard />
                  </Suspense>
                ) : (
                  <Navigate to="/auctions" replace />
                )
              }
            />
          </Route>

          <Route element={<ProtectedRoute permission={PERMISSIONS.Auction_AuctionClerk} />}>
            <Route
              path="/messenger/:id"
              element={
                <>
                  <title>{getPageTitle("Messenger")}</title>
                  <Suspense
                    fallback={
                      <div className="flex h-svh">
                        <Loader />
                      </div>
                    }
                  >
                    <Messenger />
                  </Suspense>
                </>
              }
            />
          </Route>
        </Routes>
      </BrowserRouter>
      </ErrorBoundary>
      <BackToTop />
      <ClarityScript />
      <DisclaimerModal
        isOpen={disclaimerOpen}
        setIsOpen={handleDisclaimerClose}
        content={disclaimerContent}
      />
    </>
  );
}

export default App;
